import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createCliente, createIndirizzo } from '@/api/clienti'
import { useToast } from '@/hooks/use-toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { User, MapPin, Plus, Trash2 } from 'lucide-react'
import ClienteFormFields from './ClienteFormFields'
import IndirizzoFormFields from './IndirizzoFormFields'
import VenditoreSelector from '@/components/venditori/VenditoreSelector'
import { validateReferenteAzienda } from '@/utils/validation'

const INITIAL_CLIENTE_DATA = {
  tipo: 'privato',
  nome: '',
  cognome: '',
  codice_fiscale: '',
  ragione_sociale: '',
  partita_iva: '',
  codice_sdi: '',
  email: '',
  telefono: '',
  cellulare: '',
  pec: '',
  note: '',
  attivo: true,
  disponibile_ecommerce: false,
  venditore: '',
  referente_azienda: { nome: '', cognome: '', telefono: '', email: '' },
}

const createInitialIndirizzo = (tipo, isDefault = false) => ({
  tipo,
  destinatario: '',
  via: '',
  civico: '',
  cap: '',
  citta: '',
  provincia: '',
  nazione: 'IT',
  note: '',
  default: isDefault,
})

const hasAddressData = (indirizzo) => [
  indirizzo.destinatario,
  indirizzo.via,
  indirizzo.civico,
  indirizzo.cap,
  indirizzo.citta,
  indirizzo.provincia,
].some((value) => value?.trim())

function OptionalAddressSection({
  title,
  description,
  visible,
  onShow,
  onRemove,
  formData,
  errors,
  onChange,
  onSwitchChange,
  idPrefix,
  showDestinatario = true,
  showAddressLabel = true,
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-lg font-semibold">{title}</h3>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
        {!visible ? (
          <Button type="button" variant="outline" size="sm" onClick={onShow}>
            <Plus className="h-4 w-4 mr-2" />
            Aggiungi
          </Button>
        ) : (
          <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
            <Trash2 className="h-4 w-4 mr-2" />
            Rimuovi
          </Button>
        )}
      </div>

      {visible && (
        <IndirizzoFormFields
          formData={formData}
          errors={errors}
          onChange={onChange}
          onSwitchChange={onSwitchChange}
          compact={true}
          idPrefix={idPrefix}
          showTipo={false}
          showDestinatario={showDestinatario}
          showAddressLabel={showAddressLabel}
        />
      )}
    </div>
  )
}

/**
 * Dialog per creazione cliente completo.
 * Include tutti i campi identici a /clienti/nuovo + possibilità di aggiungere indirizzo.
 * 
 * @param {Object} props
 * @param {boolean} props.open - Dialog aperto/chiuso
 * @param {Function} props.onOpenChange - Callback cambio stato
 * @param {Function} props.onSuccess - Callback cliente creato con successo
 */
export default function ClienteQuickCreateDialog({ open, onOpenChange, onSuccess }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState(INITIAL_CLIENTE_DATA)
  const [errors, setErrors] = useState({})
  const [venditoreSelezionato, setVenditoreSelezionato] = useState(null)
  const [showFatturazione, setShowFatturazione] = useState(true)
  const [showSpedizione, setShowSpedizione] = useState(false)
  const [fatturazioneData, setFatturazioneData] = useState(() => createInitialIndirizzo('fatturazione', true))
  const [spedizioneData, setSpedizioneData] = useState(() => createInitialIndirizzo('spedizione'))
  const [fatturazioneErrors, setFatturazioneErrors] = useState({})
  const [spedizioneErrors, setSpedizioneErrors] = useState({})

  const createClienteMutation = useMutation({
    mutationFn: createCliente,
    onSuccess: async (response) => {
      const cliente = response.data
      
      const indirizziDaCreare = [
        showFatturazione && hasAddressData(fatturazioneData) ? fatturazioneData : null,
        showSpedizione && hasAddressData(spedizioneData) ? spedizioneData : null,
      ].filter(Boolean)

      try {
        await Promise.all(indirizziDaCreare.map((indirizzo) => createIndirizzo({
          ...indirizzo,
          provincia: indirizzo.provincia.toUpperCase(),
          nazione: indirizzo.nazione.toUpperCase(),
          cliente: cliente.id,
        })))
      } catch (error) {
        console.error('Errore creazione indirizzi:', error)
        toast({
          title: 'Cliente creato, indirizzo non salvato',
          description: 'Apri la scheda cliente e completa nuovamente l’indirizzo.',
          variant: 'destructive',
        })
      }
      
      queryClient.invalidateQueries({ queryKey: ['clienti'] })
      toast({
        title: 'Cliente creato',
        description: `${cliente.nome_completo} è stato creato con successo.`,
      })
      onSuccess?.(cliente)
      handleClose()
    },
    onError: (error) => {
      const errorData = error.response?.data
      if (errorData) {
        setErrors(errorData)
      }
      toast({
        title: 'Errore',
        description: 'Impossibile creare il cliente. Verifica i dati inseriti.',
        variant: 'destructive',
      })
    },
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name.startsWith('referente_')) {
      const field = name.slice('referente_'.length)
      setFormData(prev => ({
        ...prev,
        referente_azienda: { ...prev.referente_azienda, [field]: value },
      }))
      setErrors(prev => ({
        ...prev,
        referente_azienda: { ...prev.referente_azienda, [field]: null },
      }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
      if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const handleSwitchChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }))
  }

  const handleIndirizzoChange = (setData, setAddressErrors) => (e) => {
    const { name, value } = e.target
    setData(prev => ({ ...prev, [name]: value }))
    setAddressErrors(prev => ({ ...prev, [name]: null }))
  }

  const handleIndirizzoSwitchChange = (setData) => (checked) => {
    setData(prev => ({ ...prev, default: checked }))
  }

  const handleVenditoreChange = (venditore) => {
    setVenditoreSelezionato(venditore)
    setFormData(prev => ({ ...prev, venditore: venditore?.id || '' }))
    setErrors(prev => ({ ...prev, venditore: null }))
  }

  const handleClose = () => {
    setFormData(INITIAL_CLIENTE_DATA)
    setErrors({})
    setVenditoreSelezionato(null)
    setShowFatturazione(true)
    setShowSpedizione(false)
    setFatturazioneData(createInitialIndirizzo('fatturazione', true))
    setSpedizioneData(createInitialIndirizzo('spedizione'))
    setFatturazioneErrors({})
    setSpedizioneErrors({})
    onOpenChange(false)
  }

  const validateAddress = (indirizzo, visible, setAddressErrors) => {
    if (!visible || !hasAddressData(indirizzo)) {
      setAddressErrors({})
      return true
    }

    const addressErrors = {}
    if (!indirizzo.via) addressErrors.via = 'Via obbligatoria'
    if (!indirizzo.cap) addressErrors.cap = 'CAP obbligatorio'
    if (!indirizzo.citta) addressErrors.citta = 'Città obbligatoria'
    if (!indirizzo.provincia) addressErrors.provincia = 'Provincia obbligatoria'
    if (indirizzo.nazione === 'IT' && indirizzo.cap && !/^\d{5}$/.test(indirizzo.cap)) {
      addressErrors.cap = 'Il CAP italiano deve essere di 5 cifre'
    }
    if (indirizzo.nazione === 'IT' && indirizzo.provincia && !/^[A-Za-z]{2}$/.test(indirizzo.provincia)) {
      addressErrors.provincia = 'La provincia deve essere di 2 lettere'
    }
    setAddressErrors(addressErrors)
    return Object.keys(addressErrors).length === 0
  }

  const validateForm = () => {
    const newErrors = {}

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email non valida'
    }

    if (formData.tipo === 'privato' && !formData.telefono) {
      newErrors.telefono = 'Telefono obbligatorio'
    }
    if (!formData.venditore) {
      newErrors.venditore = 'Seleziona il venditore che ha acquisito il contatto'
    }
    if (formData.pec && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.pec)) {
      newErrors.pec = 'PEC non valida'
    }

    if (formData.tipo === 'privato') {
      if (!formData.nome) newErrors.nome = 'Nome obbligatorio'
      if (!formData.cognome) newErrors.cognome = 'Cognome obbligatorio'
      if (formData.codice_fiscale && formData.codice_fiscale.length !== 16) {
        newErrors.codice_fiscale = 'Il codice fiscale deve essere di 16 caratteri'
      }
    }

    if (formData.tipo === 'azienda') {
      if (!formData.ragione_sociale) {
        newErrors.ragione_sociale = 'Ragione sociale obbligatoria'
      }
      if (formData.partita_iva && formData.partita_iva.length !== 11) {
        newErrors.partita_iva = 'La partita IVA deve essere di 11 caratteri'
      }
      if (formData.codice_sdi && formData.codice_sdi.length !== 7) {
        newErrors.codice_sdi = 'Il codice SDI deve essere di 7 caratteri'
      }
      const referenteErrors = validateReferenteAzienda(formData.referente_azienda)
      if (Object.keys(referenteErrors).length) newErrors.referente_azienda = referenteErrors
    }

    setErrors(newErrors)
    
    const fatturazioneValida = validateAddress(fatturazioneData, showFatturazione, setFatturazioneErrors)
    const spedizioneValida = validateAddress(spedizioneData, showSpedizione, setSpedizioneErrors)

    return Object.keys(newErrors).length === 0 && fatturazioneValida && spedizioneValida
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const dataToSend = { ...formData }
    
    if (formData.tipo === 'privato') {
      delete dataToSend.ragione_sociale
      delete dataToSend.partita_iva
      delete dataToSend.codice_sdi
      delete dataToSend.referente_azienda
      if (!dataToSend.codice_fiscale) delete dataToSend.codice_fiscale
    } else {
      delete dataToSend.nome
      delete dataToSend.cognome
      delete dataToSend.codice_fiscale
      if (!dataToSend.partita_iva) delete dataToSend.partita_iva
      if (!dataToSend.codice_sdi) delete dataToSend.codice_sdi
    }

    if (!dataToSend.cellulare) delete dataToSend.cellulare
    if (!dataToSend.pec) delete dataToSend.pec
    if (!dataToSend.note) delete dataToSend.note
    if (!dataToSend.email) delete dataToSend.email
    if (formData.tipo === 'azienda' && !dataToSend.telefono) delete dataToSend.telefono

    createClienteMutation.mutate(dataToSend)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Crea Nuovo Cliente
          </DialogTitle>
          <DialogDescription>
            Inserisci i dati del cliente e gli indirizzi di fatturazione e spedizione.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {/* Form Cliente - usa componente riutilizzabile */}
          <ClienteFormFields
            formData={formData}
            errors={errors}
            onChange={handleChange}
            onSwitchChange={handleSwitchChange}
            venditoreField={(
              <VenditoreSelector
                value={venditoreSelezionato}
                onChange={handleVenditoreChange}
                error={errors.venditore}
              />
            )}
            showCards={false}
            compact={true}
            billingSection={(
              <OptionalAddressSection
                title="Indirizzo di Fatturazione"
                description="Indirizzo completo da usare per documenti e fatture."
                visible={showFatturazione}
                onShow={() => setShowFatturazione(true)}
                onRemove={() => {
                  setShowFatturazione(false)
                  setFatturazioneData(createInitialIndirizzo('fatturazione', true))
                  setFatturazioneErrors({})
                }}
                formData={fatturazioneData}
                errors={fatturazioneErrors}
                onChange={handleIndirizzoChange(setFatturazioneData, setFatturazioneErrors)}
                onSwitchChange={handleIndirizzoSwitchChange(setFatturazioneData)}
                idPrefix="fatturazione-"
                showDestinatario={false}
                showAddressLabel={false}
              />
            )}
          />

          {/* Indirizzo di spedizione opzionale */}
          <Separator className="my-6" />
          <div className="space-y-6">
            <OptionalAddressSection
              title="Indirizzo di Spedizione"
              description="Compilalo se la merce deve essere consegnata a un indirizzo diverso."
              visible={showSpedizione}
              onShow={() => setShowSpedizione(true)}
              onRemove={() => {
                setShowSpedizione(false)
                setSpedizioneData(createInitialIndirizzo('spedizione'))
                setSpedizioneErrors({})
              }}
              formData={spedizioneData}
              errors={spedizioneErrors}
              onChange={handleIndirizzoChange(setSpedizioneData, setSpedizioneErrors)}
              onSwitchChange={handleIndirizzoSwitchChange(setSpedizioneData)}
              idPrefix="spedizione-"
            />
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createClienteMutation.isPending}
            >
              Annulla
            </Button>
            <Button type="submit" disabled={createClienteMutation.isPending}>
              {createClienteMutation.isPending ? 'Creazione...' : 'Crea Cliente'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
